import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './CSS/ProductTemplate.css';
import { initIRPlayer } from '@into-reality/player';
import useAuth from '../hooks/useAuth';

function isInsideIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function validateIframeAccess(canBeIframe, allowedReferers) {
  if (!isInsideIframe()) return { allowed: true };

  if (!canBeIframe) {
    return { allowed: false, reason: 'This content cannot be embedded.' };
  }

  if (!allowedReferers || allowedReferers.length === 0) {
    return { allowed: false, reason: 'This content is not authorized for embedding.' };
  }
  
  // Wildcard: allow all domains
  if (allowedReferers.includes('*')) {
    return { allowed: true };
  }

  const referrer = document.referrer;
  if (!referrer) {
    return { allowed: false, reason: 'This content is not authorized for this domain.' };
  }

  try {
    const referrerHost = new URL(referrer).hostname;
    const isAllowed = allowedReferers.some(allowed => {
      const normalizedAllowed = allowed.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      return referrerHost === normalizedAllowed || referrerHost.endsWith('.' + normalizedAllowed);
    });

    if (!isAllowed) {
      return { allowed: false, reason: 'This content is not authorized for this domain.' };
    }
  } catch {
    return { allowed: false, reason: 'This content is not authorized for this domain.' };
  }

  return { allowed: true };
}

function ProductTemplate() {
  const navigate = useNavigate();
  const location = useLocation();

  const rawProductId = useParams().productId;
  const productId = useMemo(() => rawProductId.replace(/\/$/, ''), [rawProductId]);

  const [event, setEvent] = useState(null);
  const [livekitSession, setLivekitSession] = useState(null); // { token, wsUrl, room } — solo si el evento va en modo LiveKit
  const [error, setError] = useState(null);
  const [accessMessage, setAccessMessage] = useState('');
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const hasStartedFetchRef = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { userData, authLoading } = useAuth(false);

  // Back button goes home
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handleBackButton = (event) => {
      event.preventDefault();
      window.location.href = '/';
    };
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  useEffect(() => {
    window.isProductTemplateLoaded = true;
    const loadEvent = new Event('load');
    window.dispatchEvent(loadEvent);

    const loadingTimer = setTimeout(() => setShowLoadingMessage(true), 250);
    let isCancelled = false;

    const fetchEventNoAuth = async (eventId) => {
      if (isCancelled) return;
      const r = await fetch(`${backendUrl}/event/get/${eventId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const j = await r.json();
      if (isCancelled) return;
      if (j?.success && j?.event) {
        clearTimeout(loadingTimer);
        setEvent(j.event);
      } else {
        clearTimeout(loadingTimer);
        setError('Event not found');
      }
    };

    const fetchEventWithAuth = async (eventId, token) => {
      if (isCancelled) return;
      const r = await fetch(`${backendUrl}/event/get/${eventId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'auth-token': token },
      });
      const j = await r.json();
      if (isCancelled) return;
      if (j?.success && j?.event) {
        clearTimeout(loadingTimer);
        setEvent(j.event);
      } else {
        clearTimeout(loadingTimer);
        setError('Event not found');
      }
    };

    // Pide el JWT de espectador LiveKit una vez confirmado el acceso. El backend
    // aplica la misma puerta que /permissions/check y responde 400 si el evento
    // no corre en modo LiveKit — en ese caso (o ante cualquier fallo) se ignora
    // y el player DASH sigue siendo el camino normal.
    const fetchLivekitToken = async (token) => {
      try {
        // Vendor-neutral route name: this URL shows in every viewer's Network tab.
        const r = await fetch(`${backendUrl}/permissions/livekit-token/${productId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-token': token },
        });
        if (isCancelled || !r.ok) return;
        const j = await r.json();
        if (isCancelled || !j?.success || !j?.token) return;
        setLivekitSession({ token: j.token, wsUrl: j.wsUrl, room: j.room });
      } catch (e) {
        // Sin LiveKit no se degrada nada: el evento se reproduce por DASH.
      }
    };

    const run = async () => {
      if (isCancelled) return;

      if (hasStartedFetchRef.current) {
        console.log('Fetch already in progress, skipping');
        return;
      }

      hasStartedFetchRef.current = true;
      setError(null);

      // Send auth token if available
      const authToken = localStorage.getItem('auth-token');
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['auth-token'] = authToken;

      try {
        const productResponse = await fetch(`${backendUrl}/product/get/${productId}`, {
          method: 'GET',
          headers,
        });

        if (isCancelled) return;

        // If backend blocks anonymous for private items, it may return 401/403
        if (productResponse.status === 401 || productResponse.status === 403) {
          if (isInsideIframe()) {
            clearTimeout(loadingTimer);
            setAccessMessage('This content is not available for embedding.');
            return;
          }
          // Fall through to PRIVATE FLOW
        } else if (!productResponse.ok) {
          clearTimeout(loadingTimer);
          setError('Failed to fetch product');
          return;
        } else {
          const productData = await productResponse.json();
          if (!productData?.success || !productData?.product) {
            clearTimeout(loadingTimer);
            setError('Product not found');
            return;
          }

          const product = productData.product;
          if (!product.status) {
            clearTimeout(loadingTimer);
            setError('This product is inactive.');
            return;
          }

          const eventId = product?.event?._id;
          if (!eventId) {
            clearTimeout(loadingTimer);
            setError('Event not found');
            return;
          }
          window.eventId = eventId;

          if (product.isPublic === true) {
            // PUBLIC FLOW — with auth if available
            if (authToken) {
              await fetchEventWithAuth(eventId, authToken);
              fetchLivekitToken(authToken); // anónimos no: el endpoint requiere sesión
            } else {
              await fetchEventNoAuth(eventId);
            }
            return;
          }

          if (isInsideIframe()) {
            // Iframe access: fetch event without auth
            await fetchEventNoAuth(eventId);
            return;
          }
          // else fall through to PRIVATE FLOW
        }
      } catch (e) {
        console.error('Error fetching product:', e);
        // Network/parse issues — continue to private flow
      }

      // PRIVATE FLOW
      if (isCancelled) return;
      if (authLoading) return;

      if (!userData) {
        navigate('/account', { state: { from: location } });
        return;
      }

      const authToken2 = localStorage.getItem('auth-token');
      if (!authToken2) {
        navigate('/account', { state: { from: location } });
        return;
      }

      try {
        const perm = await fetch(`${backendUrl}/permissions/check/${productId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-token': authToken2 },
        });
        if (isCancelled) return;

        const permData = await perm.json();
        if (!permData?.hasPermission) {
          clearTimeout(loadingTimer);
          navigate('/');
          return;
        }

        const productResp = await fetch(`${backendUrl}/product/get/${productId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-token': authToken2 },
        });
        if (isCancelled) return;

        const productData = await productResp.json();
        if (!productData?.success || !productData?.product) {
          clearTimeout(loadingTimer);
          setError('Product not found');
          return;
        }

        const product = productData.product;
        if (!product.status) {
          clearTimeout(loadingTimer);
          setError('This product is inactive.');
          return;
        }

        const eventId = product?.event?._id;
        if (!eventId) {
          clearTimeout(loadingTimer);
          setError('Event not found');
          return;
        }
        window.eventId = eventId;

        await fetchEventWithAuth(eventId, authToken2);
        fetchLivekitToken(authToken2);
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          clearTimeout(loadingTimer);
          setError('An error occurred while fetching the product or event');
        }
      } finally {
        hasStartedFetchRef.current = false;
      }
    };

    run();

    return () => {
      isCancelled = true;
      window.isProductTemplateLoaded = false;
      clearTimeout(loadingTimer);
      hasStartedFetchRef.current = false;
    };
  }, [productId, backendUrl, authLoading, userData, navigate, location]);

  useEffect(() => {
    if (livekitSession) {
      // Deliberately vendor-neutral: this lands in every viewer's console.
      console.log('Low-latency session ready');
    }
  }, [livekitSession]);

  // Selección de vía, mismo patrón que el fallback DASH→HLS un nivel por
  // encima: el campo `webrtc_manifest` del Event (heredado de la época
  // mediasoup, hoy sin otro uso en esta página) actúa de interruptor explícito
  // del operador — con valor ⇒ LiveKit, vacío ⇒ DASH de siempre. El valor en
  // sí no se usa como URL: el wsUrl real llega con el token del backend, que
  // es quien sabe a qué caja LiveKit habla cada entorno.
  const useLivekit = !!event && !!(event.webrtc_manifest || '').trim();
  const playerInitRef = useRef(false);

  // Init player once event is present
  useEffect(() => {
    if (!event) return;

    // Iframe rules apply to both delivery paths
    const validation = validateIframeAccess(event.canBeIframe || false, event.allowed_referers || []);
    if (!validation.allowed) {
      setAccessMessage(validation.reason);
      return;
    }

    // LiveKit path: the player needs url+token at init time, and the token
    // arrives async after the access check — hold initialization until then.
    // The player itself decides the mode (checkWebRTCAvailability): with
    // livekit_url+token it goes WebRTC, otherwise plain DASH.
    if (useLivekit && !livekitSession) return;

    if (playerInitRef.current) return; // never double-init video.js on the same element
    const videoId = `IR-${productId}`;

    const videoElement = document.getElementById(videoId);
    if (!videoElement) {
      console.error('Video element not found. Initialization aborted.');
      return;
    }

    const eventInformation = {
      allowed_referers: event.allowed_referers || [],
      audio_imgs: event.audio_imgs || [],
      audio_names: event.audio_names || [],
      cam_imgs: event.cam_imgs || [],
      cam_names: event.cam_names || [],
      captions_imgs: event.captions_imgs || [],
      canBeIframe: event.canBeIframe || false,
      product_id: productId,
      video_types: event.video_types || [],
      sources: event.sources || [],
      sources_dash: event.sourcesDash || '',
      dash_settings: event.dashSettings || '',
      webrtc_manifest: event.webrtc_manifest || '',
      // Solo en modo LiveKit: el player oficial (WebRTCClient) conecta con
      // esto y proyecta 360 igual que con DASH — mismo elemento de vídeo.
      livekit_url: useLivekit ? livekitSession.wsUrl : '',
      livekit_token: useLivekit ? livekitSession.token : '',
    };

    window.isSyncX = false;
    playerInitRef.current = true;
    initIRPlayer(videoId, eventInformation);
    // useLivekit derives from `event`, already a dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, productId, livekitSession]);

  if (accessMessage) return (
    <div style={{
      backgroundColor: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      aspectRatio: '16/9',
    }}>
      <div style={{
        color: '#fff',
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        textAlign: 'center',
        padding: '20px',
      }}>
        {accessMessage}
      </div>
    </div>
  );

  if (error) {
    return (
      <div style={{ color: 'white' }}>
        <p>Error: {error}</p>
        <p>
          Please try again later or{' '}
          <a
            href="#"
            style={{ color: 'white', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          >
            reload the page
          </a>
          .
        </p>
      </div>
    );
  }

  if (event && useLivekit && !livekitSession) {
    // The official player initializes only once url+token are here.
    return (
      <div style={{
        backgroundColor: '#000', display: 'flex', alignItems: 'center',
        justifyContent: 'center', width: '100%', aspectRatio: '2/1',
        color: '#fff', fontFamily: 'Arial, sans-serif', fontSize: 16,
      }}>
        Preparing low-latency stream…
      </div>
    );
  }

  if (event) {
    const videoId = `IR-${productId}`;
    return (
      <video-js
        id={videoId}
        className="video-js vjs-default-skin vjs-fill intoreality-player"
        controls
        crossOrigin="anonymous"
        playsInline
        preload="auto"
      />
    );
  }

  if (showLoadingMessage) {
    return (
      <div style={{ color: 'white' }}>
        <p>Loading...</p>
        <p>
          If the product does not load,{' '}
          <a
            href="#"
            style={{ color: 'white', textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              console.warn('Manual reload triggered.');
              window.location.reload();
            }}
          >
            click here.
          </a>
        </p>
      </div>
    );
  }

  return null;
}

export default ProductTemplate;